import React from "react";

/**
 * Button component with variants and sizes
 *
 * @param {string} variant - 'primary' | 'secondary' | 'danger' | 'ghost' (default: 'primary')
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' (default: 'md')
 * @param {boolean} disabled - Whether the button is disabled
 * @param {function} onClick - Click handler
 * @param {string} className - Additional CSS classes
 * @param {ReactNode} children - Button content
 */
export default function Button({
  children,
  onClick,
  disabled,
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}) {
  // Base styles that apply to all buttons
  const baseStyles = "font-medium rounded-md transition-all duration-150 focus:outline-none focus:ring-1 disabled:opacity-50 disabled:cursor-not-allowed border";

  // Variant styles — flat hardware look (no gradients here; use HardwareButton for those)
  const variantStyles = {
    primary: "bg-accent border-blue-400 text-white hover:bg-accent-hover focus:ring-accent",
    secondary: "bg-neutral-800 border-neutral-700 text-text hover:bg-neutral-700 focus:ring-neutral-500",
    danger: "bg-red-500 border-red-400 text-white hover:bg-red-600 focus:ring-red-500",
    ghost: "bg-transparent text-text-muted border-neutral-700 hover:bg-neutral-800 hover:text-text focus:ring-accent",
  };

  // Size styles
  const sizeStyles = {
    xs: "px-2 py-1 text-xs h-6",
    sm: "px-3 py-1.5 text-sm h-8",
    md: "px-4 py-2 text-sm h-10",
    lg: "px-6 py-3 text-base h-12",
  };

  const buttonClasses = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

  return (
    <button
      className={buttonClasses}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
}
